"use client";

import type { UseFormReturn } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type {
  ComposeClientContextValue,
  ComposeRecipientsContextValue,
} from "@/lib/core/crm/types";
import type { ComposeFormValues } from "@/lib/types/compose-modal-types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { ClientEmailSelector } from "../ClientEmailSelector";

interface MoreRecipientsSectionProps {
  bccCompanyPartners: boolean;
  clientState: ComposeClientContextValue;
  form: UseFormReturn<ComposeFormValues>;
  hasReplyRestrictions: boolean;
  onBccCompanyPartnersChange: (checked: boolean) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  recipientsState: ComposeRecipientsContextValue;
}

export function MoreRecipientsSection({
  bccCompanyPartners,
  clientState,
  form,
  hasReplyRestrictions,
  onBccCompanyPartnersChange,
  onOpenChange,
  open,
  recipientsState,
}: MoreRecipientsSectionProps) {
  const {
    clientOptions,
    filteredHubUsers,
    hubUsersLoading,
    clientsLoading,
    isSearching,
    clientSearchTerm,
    onClientSearchChange,
  } = clientState;
  const { ccRecipients, bccRecipients, setCcRecipients, setBccRecipients } = recipientsState;

  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <div className="rounded-md border border-border/70 p-3">
        <CollapsibleTrigger className="flex w-full items-center justify-between text-left">
          <div>
            <p className="text-sm font-medium">More recipients</p>
            <p className="text-xs text-muted-foreground">Optional CC and BCC recipients</p>
          </div>
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 space-y-3">
          <ClientEmailSelector
            form={form}
            fieldName="cc"
            label="CC"
            placeholder="Search clients or enter CC addresses..."
            clientOptions={clientOptions}
            filteredHubUsers={filteredHubUsers}
            hubUsersLoading={hubUsersLoading}
            clientsLoading={clientsLoading}
            isSearching={isSearching}
            clientSearchTerm={clientSearchTerm}
            onClientSearchChange={onClientSearchChange}
            selectedRecipients={ccRecipients}
            onRecipientsChange={setCcRecipients}
            disabled={hasReplyRestrictions}
          />

          <ClientEmailSelector
            form={form}
            fieldName="bcc"
            label="BCC"
            placeholder="Search clients or enter BCC addresses..."
            clientOptions={clientOptions}
            filteredHubUsers={filteredHubUsers}
            hubUsersLoading={hubUsersLoading}
            clientsLoading={clientsLoading}
            isSearching={isSearching}
            clientSearchTerm={clientSearchTerm}
            onClientSearchChange={onClientSearchChange}
            selectedRecipients={bccRecipients}
            onRecipientsChange={setBccRecipients}
            disabled={hasReplyRestrictions}
          />

          {hasReplyRestrictions && (
            <p className="text-xs text-muted-foreground">
              CC and BCC recipient fields are locked for replies in this modal.
            </p>
          )}

          <div className="space-y-2 rounded border border-border/60 p-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="bcc-company-partners"
                checked={bccCompanyPartners}
                onCheckedChange={onBccCompanyPartnersChange}
              />
              <label
                htmlFor="bcc-company-partners"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                Include Internal Team in BCC
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              Adds company partner addresses to BCC when sending.
            </p>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
